import {
  AppPosition,
  EnvironmentRuntime,
  EnvironmentStatus,
  ProjectEnvironment,
} from '../../services/projectEnvironment';

/** Which screen the panel is showing. */
export type View =
  | { kind: 'list' }
  | { kind: 'details'; environment: ProjectEnvironment }
  | { kind: 'form'; environment?: ProjectEnvironment };

/** Backend rule: lowercase letters, digits and hyphens only, no whitespace. */
export const ENVIRONMENT_NAME_PATTERN = /^[a-z0-9-]+$/;

/** Kept in sync with the backend. 24 (project id) + 1 (hyphen) + 38 = 63, the
 *  DNS label limit — so the id-prefixed default URL always fits. */
export const ENVIRONMENT_NAME_MAX_LENGTH = 38;

/** A custom domain follows the same rules as a project's. */
export const CUSTOM_DOMAIN_PATTERN = /^[a-z0-9-]+$/;
export const CUSTOM_DOMAIN_MAX_LENGTH = 63;

/** A background task owns the record — poll and disable actions. Only deploy
 * has such a state: create finishes inside its own request. */
export const isBusyStatus = (status: EnvironmentStatus): boolean =>
  status === 'deploying';

/** The last deploy failed. Create cannot leave a failed record behind — it
 * either returns an environment or an error. */
export const isFailedStatus = (status: EnvironmentStatus): boolean =>
  status === 'deploy_failed';

/** Has this environment ever been deployed? Everything physical — folder,
 * containers, a URL that resolves — exists only from that point, so the
 * runtime badge and the start/stop controls are meaningless before it. */
export const isDeployed = (status: EnvironmentStatus): boolean =>
  status === 'deployed';

/** Does this environment take its code from another environment rather than
 * from the project? `type` is authoritative; the link is checked too so a
 * record whose `type` was never written still reads correctly. */
export const isSub = (environment: ProjectEnvironment): boolean =>
  environment.type === 'sub' || Boolean(environment.parent_environment_id);

/** The environment directly above this one — the one it promotes from — if it
 * is still in the list. */
export const parentOf = (
  environment: ProjectEnvironment,
  all: ProjectEnvironment[]
): ProjectEnvironment | undefined =>
  environment.parent_environment_id
    ? all.find((candidate) => candidate.id === environment.parent_environment_id)
    : undefined;

/** Names are stored lowercase — capitalize the first letter for display only. */
export const displayEnvironmentName = (name: string): string =>
  name.charAt(0).toUpperCase() + name.slice(1);

/** What the deploy button says. A main pushes the project's current code; a
 * sub only ever takes what its main is already running, so the same action is
 * named after its source — 'Promote from Dev'. Falls back to the generic word
 * when the main is not in the list we were handed. */
export const deployActionLabel = (
  environment: ProjectEnvironment,
  all: ProjectEnvironment[]
): string => {
  if (!isSub(environment)) return 'Deploy current changes';
  const parent = parentOf(environment, all);
  return parent
    ? `Promote from ${displayEnvironmentName(parent.environment_name)}`
    : 'Promote';
};

/** A pipeline: its head and every environment below it, in promotion order.
 *
 * The chain is stored as one link per environment — each says only who it
 * promotes from. Walking it downward means flipping that once into a
 * "who promotes from me" map, which is a single pass over rows the panel has
 * already fetched, and cannot drift the way a stored child pointer could.
 *
 * The grouping is *total*: a head is an environment with no parent **or** one
 * whose parent is not in the list, so a record left pointing at something
 * deleted becomes its own chain instead of disappearing. A list that silently
 * drops a row is worse than one that shows an odd row.
 */
export interface EnvironmentGroup {
  /** The environment fed by Current app. */
  head: ProjectEnvironment;
  /** Head first, then each environment that promotes from the one before it. */
  chain: ProjectEnvironment[];
}

export const groupEnvironments = (all: ProjectEnvironment[]): EnvironmentGroup[] => {
  const byId = new Map(all.map((e) => [e.id, e]));
  const childOf = new Map<string, ProjectEnvironment>();
  for (const environment of all) {
    const parentId = environment.parent_environment_id;
    if (parentId) childOf.set(parentId, environment);
  }

  const heads = all.filter(
    (e) => !e.parent_environment_id || !byId.has(e.parent_environment_id)
  );

  return heads.map((head) => {
    const chain: ProjectEnvironment[] = [];
    const seen = new Set<string>();
    let cur: ProjectEnvironment | undefined = head;
    // The visited set is not decoration: bad data could produce a loop, and a
    // panel that hangs is worse than one that renders a short chain.
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      chain.push(cur);
      cur = childOf.get(cur.id);
    }
    return { head, chain };
  });
};

/** The environments a new one may be added after: the end of each chain.
 *
 * A chain grows only at its end, so anything that already has something
 * promoting from it is not offered. Derived from the list the panel already
 * holds — the same reverse map `groupEnvironments` builds. */
export const chainEnds = (all: ProjectEnvironment[]): ProjectEnvironment[] => {
  const taken = new Set(
    all.map((e) => e.parent_environment_id).filter(Boolean) as string[]
  );
  return all.filter((e) => !taken.has(e.id));
};

/** "3 changes" / "1 change". */
const changeCount = (n: number | null | undefined): string => {
  const value = n ?? 0;
  return `${value} ${value === 1 ? 'change' : 'changes'}`;
};

/** What this environment's next deploy would take from — named the way the
 * create form names it, so the two never disagree. */
export const pipelineSourceName = (
  environment: ProjectEnvironment,
  all: ProjectEnvironment[]
): string => {
  if (!isSub(environment)) return 'Current app';
  const main = parentOf(environment, all);
  return main ? displayEnvironmentName(main.environment_name) : 'its source';
};

/** The short hash of what an environment actually runs.
 *
 * The identifier shown on screen. Checkpoint titles are whole sentences
 * written by the agent — they wrap, they push the distance off the line, and
 * five of them stacked read as prose rather than as a list. The title stays
 * available as a tooltip and in the details disclosure; the hash is what the
 * row carries.
 *
 * Reads `commit_hash`, never `deployed_commit_hash`: when an environment was
 * deployed from a rollback, the stored hash is the rollback commit, which
 * carries its target's tree and is never what the user should be shown. */
export const commitName = (environment: ProjectEnvironment): string | null => {
  const commit = environment.commit_hash || environment.deployed_commit_hash;
  return commit ? commit.slice(0, 7) : null;
};

/** The checkpoint that commit was created under, for a tooltip or the details
 * disclosure. Null for a `/git/push`, the initial commit, an external pull. */
export const checkpointTitle = (environment: ProjectEnvironment): string | null =>
  environment.checkpoint_title || null;

/** The same pair, for the fork an environment diverged at. */
export const forkName = (environment: ProjectEnvironment): string | null =>
  environment.fork_commit_hash ? environment.fork_commit_hash.slice(0, 7) : null;

export const forkTitle = (environment: ProjectEnvironment): string | null =>
  environment.fork_checkpoint_title || null;

/** How loudly a position should read.
 *
 * Colour here means *risk*, not distance — so the ramp follows what a deploy
 * would cost, not how far apart the two sides are:
 *
 * * behind / up to date → nothing is lost, so no colour at all
 * * ahead               → this environment holds work its source dropped
 * * diverged            → that, plus it is behind as well
 *
 * `warning` (#dc6803) and `error` (#d92d20) are two hue-steps apart and read as
 * the same warm colour at `text-xs`, which is why "behind" is not one of them:
 * three states need three visibly different treatments, and grey → orange →
 * red is unmistakable where orange → red was not.
 *
 * One function, used by the list row and the details block, so the two cannot
 * drift into disagreeing about how bad something is.
 */
export const pipelineTone = (
  environment: ProjectEnvironment
): 'tertiary' | 'warning' | 'error' => {
  switch (environment.pipeline_state) {
    // A deploy would discard work this environment is running. Both cases are
    // the same severity, so they read the same.
    case 'diverged':
    case 'ahead':
      return 'error';
    // Behind is the ordinary state and nothing is lost by it — but it *is* the
    // one the design calls out, because it is the one you act on.
    case 'behind':
      return 'warning';
    // Up to date, and never deployed, sit at the weight of the commit hash
    // beside them: there is nothing to do about either.
    default:
      return 'tertiary';
  }
};

/** The version position as a sentence, for the callout at the top of the
 * details panel.
 *
 * Written from the *source's* point of view — "Dev is 3 changes ahead", not
 * "3 behind Dev" — because the reader is looking at this environment and the
 * thing that moved is the other one. The bare counts are what the list row
 * carries; here there is room to finish the thought, so each sentence ends on
 * what the button would actually do.
 *
 * A head environment takes the project's own code rather than another
 * environment's, so its source is "the app" and its verb is Deploying. The
 * source hash comes from `currentApp` in that case and from the parent's
 * deployed commit in the other.
 *
 * Null when there is nothing honest to say — never deployed (the Version row
 * already says so), or a position we could not work out.
 */
export const pipelineNarrative = (
  environment: ProjectEnvironment,
  all: ProjectEnvironment[],
  currentApp?: AppPosition | null
): string | null => {
  const behind = environment.changes_behind ?? 0;
  const ahead = environment.changes_ahead ?? 0;
  const me = displayEnvironmentName(environment.environment_name);

  const sub = isSub(environment);
  const main = parentOf(environment, all);
  // Mid-sentence form. Capitalized by `cap` where a sentence starts on it, so
  // "the app has rolled back" and "The app is 3 changes ahead" both read.
  const source = sub ? pipelineSourceName(environment, all) : 'the app';
  const cap = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
  const verb = sub ? 'Promoting' : 'Deploying';

  // Where the next deploy would bring this environment to. Absent when the
  // source has never been deployed, or when we have no position for the
  // project — the sentence then drops the parenthetical rather than
  // inventing one.
  const at = sub ? main?.commit_hash || main?.deployed_commit_hash : currentApp?.commit_hash;
  const short = at ? at.slice(0, 7) : null;

  switch (environment.pipeline_state) {
    case 'in_sync':
      return sub
        ? `${me} is running the same version as ${source}.`
        : `${me} is running the latest code.`;

    case 'behind': {
      const target = sub ? `the version ${source} is running` : 'the latest code';
      return (
        `${cap(source)} is ${changeCount(behind)} ahead. ` +
        `${verb} brings ${me} to ${target}${short ? ` (${short})` : ''}.`
      );
    }

    case 'ahead':
      return (
        `${me} is running ${changeCount(ahead)} that ${source} has rolled back. ` +
        `${verb} discards ${ahead === 1 ? 'it' : 'them'}` +
        `${short ? ` and moves ${me} back to ${short}` : ''}.`
      );

    case 'diverged': {
      const fork = forkName(environment);
      return (
        `${cap(source)} is ${changeCount(behind)} ahead, and ${me} is running ` +
        `${changeCount(ahead)} that ${source} has rolled back. ` +
        `${verb} discards ${ahead === 1 ? 'that change' : 'those changes'}` +
        `${short ? ` and brings ${me} to ${short}` : ''}.` +
        (fork ? ` Split at ${fork}.` : '')
      );
    }

    default:
      return null;
  }
};

/** The same fact, sized for a list row.
 *
 * Each row names its own source. It did not used to: every sub shared one
 * main, named once in a caption above them, so repeating it per line said the
 * same word three times. In a chain each environment promotes from a different
 * one, so a single caption would be wrong and the name has to come back onto
 * the row. Counts stay bare — "2 behind Stage", not "2 changes behind Stage" —
 * and the long form survives as the row's tooltip. */
export const pipelineSummary = (
  environment: ProjectEnvironment,
  all: ProjectEnvironment[]
): string | null => {
  const behind = environment.changes_behind ?? 0;
  const ahead = environment.changes_ahead ?? 0;
  const source = pipelineSourceName(environment, all);

  switch (environment.pipeline_state) {
    case 'in_sync':
      return `Up to date with ${source}`;
    case 'behind':
      return `${changeCount(behind)} behind ${source}`;
    case 'ahead':
      return `${changeCount(ahead)} ahead of ${source}`;
    case 'diverged':
      return `${changeCount(behind)} behind ${source} · ${changeCount(ahead)} ahead`;
    default:
      return null;
  }
};

/** Needs the user's eye: behind its source, diverged from it, or last deploy
 * failed. Used to mark a collapsed group — folding a pipeline away must never
 * be able to hide the reason you would have opened it. */
export const needsAttention = (environment: ProjectEnvironment): boolean =>
  environment.pipeline_state === 'behind' ||
  environment.pipeline_state === 'ahead' ||
  environment.pipeline_state === 'diverged' ||
  isFailedStatus(environment.status);

/** Short display label for an environment URL — the '<project or domain>-<name>'
 * hostname segment, without protocol and domain suffix. */
export const urlLabel = (url: string): string => {
  try {
    return new URL(url).hostname.split('.')[0];
  } catch {
    return url;
  }
};

/** Which lifecycle action the runtime state allows right now, if any.
 * null while a start/stop is in flight or when Docker is unreachable.
 *
 * A never-deployed environment reports 'stopped' — it has no containers — but
 * Start cannot help it: there is nothing built to start, and the backend
 * answers 400 "Deploy this environment before starting it." Deploy is the only
 * action that moves it forward, so no lifecycle action is offered. */
export const runtimeAction = (
  runtime: EnvironmentRuntime | undefined,
  status: EnvironmentStatus
): 'start' | 'stop' | null => {
  if (!isDeployed(status)) return null;
  if (!runtime || runtime.in_progress) return null;
  if (runtime.status === 'running' || runtime.status === 'partial') return 'stop';
  if (runtime.status === 'stopped' || runtime.status === 'error') return 'start';
  return null;
};

/** Pull a human-readable message out of an axios error, falling back to a default.
 *  Deploy errors arrive as a dict body ({detail: {detail, direction, ...}}) so the
 *  refusal can carry structured fields alongside the message. */
export const extractErrorDetail = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (typeof detail?.detail === 'string') return detail.detail;
  if (typeof detail?.message === 'string') return detail.message;
  return fallback;
};
